import { useState, useEffect, ReactNode } from 'react';
import { authApi, type User } from '../api/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext, type AuthContextType } from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  const { data: userData, isLoading, refetch: refetchUser } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.getMe(),
    retry: false,
    enabled: !!authApi.getToken(),
  });

  useEffect(() => {
    if (userData?.user) {
      setUser(userData.user);
    } else if (userData === null) {
      setUser(null);
    }
  }, [userData]);

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login({ email, password }),
    onSuccess: (data) => {
      setUser(data.user);
      queryClient.setQueryData(['auth', 'me'], { user: data.user });
    },
  });

  const registerMutation = useMutation({
    mutationFn: ({
      email,
      password,
      username,
    }: {
      email: string;
      password: string;
      username?: string;
    }) => authApi.register({ email, password, username }),
    onSuccess: (data) => {
      setUser(data.user);
      queryClient.setQueryData(['auth', 'me'], { user: data.user });
    },
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const register = async (email: string, password: string, username?: string) => {
    await registerMutation.mutateAsync({ email, password, username });
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
    queryClient.clear();
  };

  const handleRefetchUser = async () => {
    try {
      await refetchUser();
    } catch (_error) {
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    register,
    logout,
    refetchUser: handleRefetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
